import {
  findingClassificationResponse,
  findingConformityResponse,
  findingDetailResponse,
  findingObservationStatusResponse,
  findingVisitResponse,
  type FindingClassificationBody,
  type FindingClassificationResponse,
  type FindingConformityBody,
  type FindingConformityResponse,
  type FindingDetailResponse,
  type FindingObservationStatusBody,
  type FindingObservationStatusResponse,
  type FindingVisitBody,
  type FindingVisitResponse,
} from "@bitacorapro/api-types";
import { request } from "../client";

// GET /findings/:id (ADR-0085) — the observación detail screen's read.
export async function getFinding(id: string): Promise<FindingDetailResponse> {
  const json = await request<unknown>("GET", "core", `/findings/${id}`);
  return findingDetailResponse.parse(json);
}

// ADR-0134: /findings/:id/delegate and /delegation-status answer 410
// DELEGATION_RETIRED for every recipe with an observation graph, which is
// every recipe this dashboard serves an observation surface for. Their
// clients are gone; assignment is /findings/:id/assign (endpoints/
// providers.ts) and the lifecycle is the thread below.

// ADR-0058/ADR-0105 — the observation thread (postventa and, since
// ADR-0134, facility). The server enforces the transition graph
// (validateObservationTransition); the UI only ever OFFERS what
// legalObservationTargets says is legal, so a 409 here means the row
// moved under us — refetch, don't retry.
export async function updateFindingObservationStatus(
  id: string,
  body: FindingObservationStatusBody
): Promise<FindingObservationStatusResponse> {
  const json = await request<unknown>("POST", "core", `/findings/${id}/observation-status`, {
    body,
  });
  return findingObservationStatusResponse.parse(json);
}

// POST /findings/:id/conformity — the closure lane's verdict on a
// resolved observation. Conforme stamps the overlay; No conforme reopens
// with a new cycle and owes a reason (ADR-0134 §5: the supervisor's
// photos are optional, the reason is not — the server enforces it with
// NOT_CONFORM_REASON_REQUIRED).
export async function postFindingConformity(
  id: string,
  body: FindingConformityBody
): Promise<FindingConformityResponse> {
  const json = await request<unknown>("POST", "core", `/findings/${id}/conformity`, { body });
  return findingConformityResponse.parse(json);
}

// F8 (fase 2, corte 6) — agendar o cambiar la visita del contratista
// mientras la observación está en sus manos. La clave de idempotencia la
// fija quien llama por intento (un reintento tras una respuesta perdida
// recibe la respuesta original, no un segundo correo).
export async function scheduleFindingVisit(
  id: string,
  body: FindingVisitBody,
  opts: { idempotencyKey: string }
): Promise<FindingVisitResponse> {
  const json = await request<unknown>("POST", "core", `/findings/${id}/visit`, {
    body,
    headers: { "Idempotency-Key": opts.idempotencyKey },
  });
  return findingVisitResponse.parse(json);
}

// ADR-0129 — human correction of the taxonomy classification. The
// assignment modal sends especialidad/partida through here (the
// assignment IS the especialidad classification, spec §3) before the
// asignada transition; the server journals the (sugerencia, final)
// pair and re-derives severity — this client never computes either.
export async function classifyFinding(
  id: string,
  body: FindingClassificationBody
): Promise<FindingClassificationResponse> {
  const json = await request<unknown>("POST", "core", `/findings/${id}/classification`, { body });
  return findingClassificationResponse.parse(json);
}
